// src/pages/task/TaskAdminPage.tsx
// Task administration: the tenant-level configuration that governs how tasks
// behave — custom fields, categories, numbering, policies, container configs.
// Task types and custom statuses keep their existing homes and are linked here.

import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Card, CardContent } from '@react/components';
import { TaskCustomFieldDefsAdmin } from '@/features/task/TaskCustomFieldDefsAdmin';
import { TaskCategoriesAdmin } from '@/features/task/TaskCategoriesAdmin';
import { TaskNumberingAdmin } from '@/features/task/TaskNumberingAdmin';
import { TaskPoliciesAdmin } from '@/features/task/TaskPoliciesAdmin';
import { TaskContainerConfigsAdmin } from '@/features/task/TaskContainerConfigsAdmin';

type Tab = 'fields' | 'categories' | 'numbering' | 'policies' | 'containers';

const TABS: { id: Tab; labelKey: string }[] = [
  { id: 'fields', labelKey: 'task.admin.tabs.fields' },
  { id: 'categories', labelKey: 'task.admin.tabs.categories' },
  { id: 'numbering', labelKey: 'task.admin.tabs.numbering' },
  { id: 'policies', labelKey: 'task.admin.tabs.policies' },
  { id: 'containers', labelKey: 'task.admin.tabs.containers' },
];

export const TaskAdminPage = observer(() => {
  const { t } = useI18n();
  // The inheritance notices on a task type link here to show the record that
  // decided a value — "open the policy «Backend»". Without honouring ?tab the
  // link lands on whichever tab happens to be first, which reads as a broken
  // link rather than a deep one.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const tab: Tab = TABS.some((entry) => entry.id === requestedTab)
    ? (requestedTab as Tab)
    : 'fields';
  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm">
          <Link to="/tasks" className="text-muted-foreground hover:text-foreground">{t('task.list')}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('task.admin.title')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('task.admin.title')}</h1>
        <div className="mt-2 flex flex-wrap gap-3 text-sm">
          <Link to="/settings/task-types" className="text-primary hover:underline">{t('task.admin.linkTypes')}</Link>
          <Link to="/tasks/settings" className="text-primary hover:underline">{t('task.admin.linkStatuses')}</Link>
        </div>
      </div>

      <Card>
        <CardContent className="p-2">
          <div className="flex flex-wrap gap-1" role="tablist" aria-label={t('task.admin.title')}>
            {TABS.map((tb) => (
              <button
                key={tb.id}
                type="button"
                role="tab"
                aria-selected={tab === tb.id}
                onClick={() => setTab(tb.id)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  tab === tb.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {t(tb.labelKey)}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {tab === 'fields' && <TaskCustomFieldDefsAdmin />}
      {tab === 'categories' && <TaskCategoriesAdmin />}
      {tab === 'numbering' && <TaskNumberingAdmin />}
      {tab === 'policies' && <TaskPoliciesAdmin />}
      {tab === 'containers' && <TaskContainerConfigsAdmin />}
    </div>
  );
});

export default TaskAdminPage;
